import * as XLSX from 'xlsx';
import { formatCurrency, formatNumber, formatDate } from '../templates/export/formatters';

class CustomTemplateService {
  constructor() {
    this.placeholders = {
      '[EMPRESA]': (data) => localStorage.getItem('empresa_nombre') || 'Mi Empresa',
      '[TELEFONO]': (data) => localStorage.getItem('empresa_telefono') || '',
      '[EMAIL]': (data) => localStorage.getItem('empresa_email') || '',
      '[WEB]': (data) => localStorage.getItem('empresa_web') || '',
      '[DIRECCION]': (data) => localStorage.getItem('empresa_direccion') || '',
      '[CLIENTE]': (data) => data.presupuesto?.cliente || '',
      '[OBRA]': (data) => data.presupuesto?.nombre_obra || '',
      '[UBICACION]': (data) => data.presupuesto?.ubicacion || '',
      '[FECHA]': (data) => formatDate(data.presupuesto?.fecha || new Date()),
      '[CODIGO]': (data) => data.presupuesto?.codigo || '',
      '[SUBTOTAL]': (data) => formatCurrency(data.totales?.costoDirecto || 0),
      '[IND]': (data) => data.porcentajes?.indirectos || 0,
      '[INDIRECTOS]': (data) => formatCurrency(data.totales?.indirectos || 0),
      '[FIN]': (data) => data.porcentajes?.financiamiento || 0,
      '[FINANCIAMIENTO]': (data) => formatCurrency(data.totales?.financiamiento || 0),
      '[UTI]': (data) => data.porcentajes?.utilidad || 0,
      '[UTILIDAD]': (data) => formatCurrency(data.totales?.utilidad || 0),
      '[IVA]': (data) => formatCurrency(data.totales?.iva || 0),
      '[TOTAL]': (data) => formatCurrency(data.totales?.total || 0)
    };
  }

  // Aplicar plantilla personalizada preservando estilos
  applyCustomTemplate(presupuesto, conceptos, porcentajes, templateData) {
    try {
      // Calcular totales
      const totales = this.calculateTotals(conceptos, porcentajes);

      // Datos para reemplazar
      const data = {
        presupuesto,
        conceptos,
        porcentajes,
        totales
      };

      // Si no hay datos del archivo guardado, usar plantilla por defecto
      if (!templateData.file || !templateData.file.workbook) {
        return this.exportDefault(data);
      }

      // Clonar el workbook original para preservar estilos
      const originalWorkbook = templateData.file.workbook;
      const sheetName = originalWorkbook.SheetNames[0];
      const originalSheet = originalWorkbook.Sheets[sheetName];

      // Leer con opciones para preservar estilos
      const sheetData = XLSX.utils.sheet_to_json(originalSheet, {
        header: 1,
        defval: '',
        raw: false,
        dateNF: 'dd/mm/yyyy'
      });

      // Encontrar la fila con [DATOS]
      let datosRowIndex = -1;
      for (let i = 0; i < sheetData.length; i++) {
        for (let j = 0; j < sheetData[i].length; j++) {
          if (sheetData[i][j] && sheetData[i][j].toString().includes('[DATOS]')) {
            datosRowIndex = i;
            break;
          }
        }
        if (datosRowIndex >= 0) break;
      }

      // Crear una copia del sheet original para preservar todos los estilos
      const newSheet = JSON.parse(JSON.stringify(originalSheet));

      // Reemplazar placeholders celda por celda preservando formato
      const range = XLSX.utils.decode_range(newSheet['!ref']);

      for (let R = range.s.r; R <= range.e.r; ++R) {
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({r: R, c: C});

          if (newSheet[cellAddress] && newSheet[cellAddress].v) {
            let cellValue = newSheet[cellAddress].v;

            // Si es string, buscar y reemplazar placeholders
            if (typeof cellValue === 'string') {
              Object.keys(this.placeholders).forEach(placeholder => {
                if (cellValue.includes(placeholder)) {
                  const value = this.placeholders[placeholder](data);
                  cellValue = cellValue.replace(placeholder, value);
                }
              });

              // Actualizar solo el valor, preservando el formato
              newSheet[cellAddress].v = cellValue;

              // Si la celda tiene formato de número/moneda, convertir a número
              if (newSheet[cellAddress].t === 'n' && typeof cellValue === 'string') {
                const numValue = parseFloat(cellValue.replace(/[^0-9.-]/g, ''));
                if (!isNaN(numValue)) {
                  newSheet[cellAddress].v = numValue;
                }
              }
            }
          }
        }
      }

      // Insertar conceptos donde está [DATOS]
      if (datosRowIndex >= 0) {
        const headerRow = datosRowIndex > 0 ? sheetData[datosRowIndex - 1] : [];

        // Obtener el estilo de la fila de [DATOS] para aplicarlo a los conceptos
        const datosRowAddress = datosRowIndex + 1; // Excel usa base 1
        const templateRowStyles = {};

        // Guardar los estilos de la fila plantilla
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({r: datosRowIndex, c: C});
          if (newSheet[cellAddress] && newSheet[cellAddress].s) {
            templateRowStyles[C] = newSheet[cellAddress].s;
          }
        }

        // Eliminar la fila [DATOS] original
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({r: datosRowIndex, c: C});
          delete newSheet[cellAddress];
        }

        // Mover todas las filas después de [DATOS] hacia abajo
        const rowsToAdd = conceptos.length - 1; // -1 porque reemplazamos [DATOS]
        if (rowsToAdd > 0) {
          // Mover filas existentes
          for (let R = range.e.r; R > datosRowIndex; R--) {
            for (let C = range.s.c; C <= range.e.c; ++C) {
              const oldAddr = XLSX.utils.encode_cell({r: R, c: C});
              const newAddr = XLSX.utils.encode_cell({r: R + rowsToAdd, c: C});

              if (newSheet[oldAddr]) {
                newSheet[newAddr] = newSheet[oldAddr];
                delete newSheet[oldAddr];
              }
            }
          }
        }

        // Insertar conceptos con el estilo de la plantilla
        conceptos.forEach((concepto, index) => {
          const rowIndex = datosRowIndex + index;

          headerRow.forEach((header, colIndex) => {
            const cellAddress = XLSX.utils.encode_cell({r: rowIndex, c: colIndex});
            const headerLower = header.toString().toLowerCase();

            let value = '';
            let type = 's'; // string por defecto

            if (headerLower.includes('código') || headerLower.includes('codigo')) {
              value = concepto.codigo || '';
            } else if (headerLower.includes('descripción') || headerLower.includes('descripcion')) {
              value = concepto.descripcion || '';
            } else if (headerLower.includes('unidad')) {
              value = concepto.unidad || '';
            } else if (headerLower.includes('cantidad')) {
              value = concepto.cantidad || 0;
              type = 'n';
            } else if (headerLower.includes('p.u') || headerLower.includes('precio')) {
              value = concepto.precio_unitario || 0;
              type = 'n';
            } else if (headerLower.includes('importe')) {
              value = concepto.importe || 0;
              type = 'n';
            }

            // Crear la celda con el valor y el estilo preservado
            newSheet[cellAddress] = {
              v: value,
              t: type
            };

            // Aplicar el estilo de la plantilla si existe
            if (templateRowStyles[colIndex]) {
              newSheet[cellAddress].s = templateRowStyles[colIndex];
            }

            // Si es número y tiene formato de moneda en la plantilla
            if (type === 'n' && templateRowStyles[colIndex] && templateRowStyles[colIndex].numFmt) {
              newSheet[cellAddress].z = templateRowStyles[colIndex].numFmt;
            }
          });
        });

        // Actualizar el rango
        range.e.r += rowsToAdd;
        newSheet['!ref'] = XLSX.utils.encode_range(range);
      }

      // Preservar todas las propiedades del sheet original
      if (originalSheet['!cols']) newSheet['!cols'] = originalSheet['!cols'];
      if (originalSheet['!rows']) newSheet['!rows'] = originalSheet['!rows'];
      if (originalSheet['!merges']) newSheet['!merges'] = originalSheet['!merges'];
      if (originalSheet['!protect']) newSheet['!protect'] = originalSheet['!protect'];
      if (originalSheet['!autofilter']) newSheet['!autofilter'] = originalSheet['!autofilter'];
      if (originalSheet['!margins']) newSheet['!margins'] = originalSheet['!margins'];
      if (originalSheet['!images']) newSheet['!images'] = originalSheet['!images'];
      if (originalSheet['!drawings']) newSheet['!drawings'] = originalSheet['!drawings'];
      if (originalSheet['!comments']) newSheet['!comments'] = originalSheet['!comments'];
      if (originalSheet['!type']) newSheet['!type'] = originalSheet['!type'];

      // Crear nuevo workbook con el sheet modificado
      const newWorkbook = XLSX.utils.book_new();

      // Copiar propiedades del workbook original
      if (originalWorkbook.Props) newWorkbook.Props = originalWorkbook.Props;
      if (originalWorkbook.Custprops) newWorkbook.Custprops = originalWorkbook.Custprops;
      if (originalWorkbook.Themes) newWorkbook.Themes = originalWorkbook.Themes;
      if (originalWorkbook.Styles) newWorkbook.Styles = originalWorkbook.Styles;
      if (originalWorkbook.Workbook) newWorkbook.Workbook = originalWorkbook.Workbook;

      XLSX.utils.book_append_sheet(newWorkbook, newSheet, sheetName);

      // Generar nombre de archivo
      const fileName = `presupuesto_${presupuesto.codigo || 'custom'}_${new Date().getTime()}.xlsx`;

      // Escribir con opciones para preservar estilos
      XLSX.writeFile(newWorkbook, fileName, {
        bookType: 'xlsx',
        bookSST: true,
        type: 'binary',
        cellStyles: true,
        cellDates: true,
        compression: true
      });

      return { success: true, fileName };

    } catch (error) {
      console.error('Error aplicando plantilla personalizada:', error);
      return { success: false, error: error.message };
    }
  }

  // Calcular totales
  calculateTotals(conceptos, porcentajes) {
    const costoDirecto = conceptos.reduce((sum, c) => sum + (c.importe || 0), 0);
    const indirectos = costoDirecto * (porcentajes.indirectos / 100);
    const financiamiento = costoDirecto * (porcentajes.financiamiento / 100);
    const utilidad = costoDirecto * (porcentajes.utilidad / 100);
    const subtotal = costoDirecto + indirectos + financiamiento + utilidad;
    const iva = subtotal * (porcentajes.iva / 100);
    const total = subtotal + iva;

    return {
      costoDirecto,
      indirectos,
      financiamiento,
      utilidad,
      subtotal,
      iva,
      total
    };
  }

  // Exportación por defecto si no hay plantilla
  exportDefault(data) {
    const wb = XLSX.utils.book_new();

    // Crear datos de la hoja
    const wsData = [
      [data.presupuesto.nombre_obra || 'PRESUPUESTO'],
      [],
      ['Cliente:', data.presupuesto.cliente],
      ['Ubicación:', data.presupuesto.ubicacion],
      ['Fecha:', formatDate(data.presupuesto.fecha)],
      [],
      ['Código', 'Descripción', 'Unidad', 'Cantidad', 'P.U.', 'Importe']
    ];

    // Agregar conceptos
    data.conceptos.forEach(c => {
      wsData.push([
        c.codigo,
        c.descripcion,
        c.unidad,
        c.cantidad,
        c.precio_unitario,
        c.importe
      ]);
    });

    // Agregar totales
    wsData.push([]);
    wsData.push(['', '', '', '', 'Subtotal:', data.totales.costoDirecto]);
    wsData.push(['', '', '', '', `Indirectos (${data.porcentajes.indirectos}%):`, data.totales.indirectos]);
    wsData.push(['', '', '', '', `Financiamiento (${data.porcentajes.financiamiento}%):`, data.totales.financiamiento]);
    wsData.push(['', '', '', '', `Utilidad (${data.porcentajes.utilidad}%):`, data.totales.utilidad]);
    wsData.push(['', '', '', '', 'IVA (16%):', data.totales.iva]);
    wsData.push(['', '', '', '', 'TOTAL:', data.totales.total]);

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    XLSX.utils.book_append_sheet(wb, ws, 'Presupuesto');

    const fileName = `presupuesto_${data.presupuesto.codigo || 'export'}_${new Date().getTime()}.xlsx`;
    XLSX.writeFile(wb, fileName);

    return { success: true, fileName };
  }

  // Guardar plantilla con el workbook completo
  saveTemplate(name, file, workbook) {
    const templates = JSON.parse(localStorage.getItem('custom_excel_templates') || '{}');

    // Leer el workbook con todas las opciones para preservar estilos
    const enhancedWorkbook = {
      ...workbook,
      SheetNames: workbook.SheetNames,
      Sheets: {}
    };

    // Copiar cada hoja con sus estilos
    workbook.SheetNames.forEach(sheetName => {
      enhancedWorkbook.Sheets[sheetName] = workbook.Sheets[sheetName];
    });

    templates[name] = {
      name,
      file: {
        name: file.name,
        workbook: enhancedWorkbook,
        sheets: workbook.SheetNames
      },
      createdAt: new Date().toISOString(),
      mapping: this.detectMapping(workbook)
    };

    localStorage.setItem('custom_excel_templates', JSON.stringify(templates));
    return true;
  }

  // Detectar mapeo automático de la plantilla
  detectMapping(workbook) {
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const data = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

    const mapping = {
      placeholders: {},
      dataRow: -1,
      headers: [],
      styles: {}
    };

    // Buscar placeholders y fila de datos
    for (let i = 0; i < data.length; i++) {
      for (let j = 0; j < data[i].length; j++) {
        const cell = data[i][j];
        if (typeof cell === 'string') {
          // Buscar placeholders
          Object.keys(this.placeholders).forEach(placeholder => {
            if (cell.includes(placeholder)) {
              mapping.placeholders[placeholder] = { row: i, col: j };
            }
          });

          // Buscar [DATOS]
          if (cell.includes('[DATOS]')) {
            mapping.dataRow = i;
            // Guardar encabezados si están en la fila anterior
            if (i > 0 && data[i - 1]) {
              mapping.headers = data[i - 1];
            }
          }
        }

        // Guardar información de estilos
        const cellAddress = XLSX.utils.encode_cell({r: i, c: j});
        if (worksheet[cellAddress] && worksheet[cellAddress].s) {
          mapping.styles[cellAddress] = worksheet[cellAddress].s;
        }
      }
    }

    return mapping;
  }
}

// Singleton
const customTemplateService = new CustomTemplateService();
export default customTemplateService;